/** The signed-in person, as the identity provider knows them. Never tenant data (D-003). */
export interface AuthUser {
  readonly uid: string;
  readonly email: string | null;
  /** The name the person goes by; null when their account has none. */
  readonly displayName: string | null;
}

/** Machine-readable sign-in failures; the UI shows `errors.<code>` from the translations (D-066). */
export type AuthErrorCode =
  | 'auth.invalid_credentials'
  | 'auth.user_disabled'
  | 'auth.too_many_attempts'
  | 'auth.network'
  /** The session can't be renewed: revoked, or the password changed elsewhere. */
  | 'auth.session_expired'
  | 'auth.unexpected';

export class AuthError extends Error {
  constructor(
    readonly code: AuthErrorCode,
    options?: ErrorOptions,
  ) {
    super(code, options);
    this.name = 'AuthError';
  }
}

/**
 * Who is signed in, and signing in and out. The app uses the brand's Firebase
 * project (firebase-authenticator.ts); tests use a fake. Members are plain
 * functions, never methods using `this`, so they can be passed around unbound,
 * as `useSyncExternalStore` needs.
 */
export interface Authenticator {
  /** Resolves once a session kept from an earlier visit has been restored, or found absent. */
  readonly ready: () => Promise<void>;
  /** The same object until the user or their profile changes, so React can compare it by reference. */
  readonly currentUser: () => AuthUser | null;
  /** Calls the listener whenever the user or their profile may have changed; returns an unsubscribe function. */
  readonly subscribe: (listener: () => void) => () => void;
  /** Rejects with an `AuthError`. */
  readonly signInWithEmailAndPassword: (email: string, password: string) => Promise<void>;
  /** With the custom token single sign-on gets from the API (D-060). Rejects with an `AuthError`. */
  readonly signInWithCustomToken: (customToken: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  /**
   * The signed-in user's ID token for the API (D-059), renewed when it is about to
   * expire, or null when nobody is signed in. `forceRefresh` gets a new one even if the
   * current one looks valid. Rejects with an `AuthError`.
   */
  readonly idToken: (options: { readonly forceRefresh: boolean }) => Promise<string | null>;
}

/**
 * Calls `onChange` when a different user signs in, or the user signs out, after
 * the session was restored. Token refreshes and repeated notifications for the
 * same user are ignored.
 */
export function onSignedInUserChange(
  authenticator: Authenticator,
  onChange: (user: AuthUser | null) => void,
): () => void {
  let previousUid = authenticator.currentUser()?.uid ?? null;
  return authenticator.subscribe(() => {
    const user = authenticator.currentUser();
    const uid = user?.uid ?? null;
    if (uid !== previousUid) {
      previousUid = uid;
      onChange(user);
    }
  });
}
